import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, defaultProps, withProps } from 'recompose';
import { get, map } from 'lodash';
import Crumb from './Crumb';

import './Breadcrumbs.scss';

function createCrumbSchema(crumbs) {
	return {
		'@context': 'http://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@id': get(crumb, 'props.id', get(crumb, 'props.to', ''))
						.split('?')
						.shift(),
					name: crumb.props.name || crumb.props.label,
				},
			})),
	};
}

export const enhance = compose(
	defaultProps({ location: {}, searchType: 'storage' }),
	withProps(
		({
			location,
			crumbs = [],
			cityUrl,
			stateUrl,
			baseUrl,
			searchType,
			searchParams,
			tertiary,
		}) => {
			const newCrumbs = [];
			const { state, fullState, subLocality, city } = location;
			const sub = subLocality || city;

			/**
			 * These are used both on facility pages and search pages.
			 * The `searchType` parameter isn't avalable on facility pages, and
			 * so the prefix should always just be `self-storage` on those pages.
			 *
			 * We need to look at the search query params to determine other prefixes,
			 * like looking at a `vehicle` param for vehicle types.
			 *
			 * We don't do this for amenties, since we can filter to multiple amenities
			 * and don't need to reflect that in the breadcrumbs.
			 *
			 * | Storage Type       | Search Type | Attribute   | Value                |
			 * |--------------------|-------------|-------------|----------------------|
			 * | Self-Storage       | storage     |             |                      |
			 * | Car                | vehicle     |             |                      |
			 * | Boat               | vehicle     | vehicle     | boat                 |
			 * | RV                 | vehicle     | vehicle     | rv                   |
			 *
			 */

			// for facilities, we'll just use this default
			let storagePrefix = 'Self-';

			const searchTypePrefixes = {
				storage: storagePrefix,
				vehicle: 'Car ', // we assume a basic vehicle search is for cars
			};

			// prefixes for keys found on the `vehicle` search param
			const vehiclePrefixes = {
				boat: 'Boat ',
				rv: 'RV ',
			};

			// starting here, we're modifying this for CLPs.
			// if the storage type matches one of the search types,
			// use that prefix from this object if there's a match.
			storagePrefix = searchTypePrefixes[searchType] || storagePrefix;

			if (searchParams && searchParams.vehicle) {
				// if there are search params, and a vehicle param, match against that
				storagePrefix =
					vehiclePrefixes[searchParams.vehicle] || storagePrefix;
			}

			newCrumbs.push(
				<Crumb
					href={`${baseUrl}`}
					id="/self-storage/"
					key="base"
					label="Home"
					name="Locations"
					baseUrl={baseUrl}
					tertiary={tertiary}
				/>,
			);

			if (stateUrl) {
				newCrumbs.push(
					<Crumb
						key="state"
						href={baseUrl + stateUrl}
						isLast={!cityUrl && !crumbs.length}
						label={fullState}
						name={state}
						className="state"
						tertiary={tertiary}
					/>,
				);
			}

			if (cityUrl) {
				const isLast = !crumbs.length;
				newCrumbs.push(
					<Crumb
						key="sublocality"
						className="sublocality"
						href={baseUrl + cityUrl}
						isLast={isLast}
						label={`${sub} Storage`}
						name={sub}
					>
						{sub}{' '}
						<span className="self-storage">
							{storagePrefix}Storage
						</span>
					</Crumb>,
				);
			}

			return {
				crumbs: [...newCrumbs, ...crumbs],
				crumbJSON: createCrumbSchema([...newCrumbs, ...crumbs]),
			};
		},
	),
);

export function Breadcrumbs({ crumbs, crumbJSON, className }) {
	return (
		<Fragment>
			<ul className={classnames('ss-breadcrumbs', className)}>
				{map(crumbs, (crumb) => (
					<li
						className="ss-breadcrumb"
						key={crumb.key}
					>
						{crumb}
					</li>
				))}
			</ul>
			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify(crumbJSON)}
				</script>
			</Helmet>
		</Fragment>
	);
}

Breadcrumbs.propTypes = {
	className: PropTypes.string,
	crumbJSON: PropTypes.object,
	crumbs: PropTypes.node,
};

export default enhance(Breadcrumbs);
